import React, { useEffect, useState, useCallback, useRef } from "react";
import "./AnimatedCard.scss";

/**
 * AnimatedCard - Modal con animación de apertura y cierre
 * @param {Object} props
 * @param {React.ReactNode} props.children - Contenido del modal
 * @param {Function} props.setClose - Callback para cerrar el modal
 * @param {String} props.className - Clases CSS adicionales
 * @param {Boolean} props.closeOnBackdropClick - Cerrar al hacer click fuera del modal (default: true)
 * @param {Boolean} props.showCloseButton - Mostrar botón de cerrar (default: true)
 * @param {Boolean} props.blockScroll - Bloquear scroll del body (default: true)
 * @param {Number} props.animationDuration - Duración de la animación en ms (default: 300)
 * @param {String} props.size - Tamaño del modal: 'sm' | 'md' | 'lg' | 'xl' | 'full' (default: 'md')
 * @param {String} props.variant - Estilo del modal: 'default' | 'gradient' | 'minimal' | 'elegant' | 'custom' (default: 'elegant')
 * @param {String} props.title - Título opcional del modal
 * @param {String} props.subtitle - Subtítulo opcional del modal
 * @param {Boolean} props.showHeader - Mostrar encabezado decorativo (default: true)
 * @param {Boolean} props.centerContent - Centrar contenido verticalmente (default: false)
 * @param {Boolean} props.coloredHeader - Mostrar título en header con fondo de color (default: false)
 */
const AnimatedCard = ({
    className = "",
    children,
    setClose,
    closeOnBackdropClick = true,
    showCloseButton = true,
    blockScroll = true,
    animationDuration = 300,
    size = "md",
    variant = "elegant",
    title = "",
    subtitle = "",
    showHeader = true,
    centerContent = false,
    coloredHeader = false,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const modalRef = useRef(null);
    const closeTimeoutRef = useRef(null);
    const isClosingRef = useRef(false);

    // Manejar el cierre del modal
    const handleClose = useCallback(() => {
        if (isClosingRef.current) return; // Prevenir múltiples clicks

        isClosingRef.current = true;
        setIsVisible(false);

        closeTimeoutRef.current = setTimeout(() => {
            if (blockScroll) {
                document.body.classList.remove("no-scroll");
            }
            setClose(false);
        }, animationDuration);
    }, [setClose, blockScroll, animationDuration]);

    // Manejar click en el backdrop
    const handleBackdropClick = useCallback(
        (e) => {
            if (
                closeOnBackdropClick &&
                modalRef.current &&
                !modalRef.current.contains(e.target)
            ) {
                handleClose();
            }
        },
        [closeOnBackdropClick, handleClose]
    );

    // Manejar tecla Escape
    const handleEscapeKey = useCallback(
        (e) => {
            if (e.key === "Escape") {
                handleClose();
            }
        },
        [handleClose]
    );

    // Efecto de montaje
    useEffect(() => {
        // Bloquear scroll si está habilitado
        if (blockScroll) {
            document.body.classList.add("no-scroll");
        }

        // Animar apertura
        const openTimeout = setTimeout(() => {
            setIsVisible(true);
        }, 10);

        // Agregar listener para tecla Escape
        document.addEventListener("keydown", handleEscapeKey);

        // Cleanup
        return () => {
            clearTimeout(openTimeout);
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            document.removeEventListener("keydown", handleEscapeKey);
            if (blockScroll) {
                document.body.classList.remove("no-scroll");
            }
        };
    }, [blockScroll, handleEscapeKey]);

    return (
        <div
            className={`animated-card animated-card--${variant} ${className} ${
                isVisible ? "animated-card--visible" : ""
            }`}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
        >
            <div
                ref={modalRef}
                className={`animated-card__content animated-card__content--${size} ${
                    centerContent ? "animated-card__content--centered" : ""
                } ${isVisible ? "animated-card__content--visible" : ""}`}
            >
                {showHeader && variant !== "minimal" && !coloredHeader && (
                    <div className="animated-card__header-decoration" />
                )}

                {showCloseButton && (
                    <button
                        className="animated-card__close-btn"
                        type="button"
                        onClick={handleClose}
                        aria-label="Cerrar modal"
                    >
                        <i className="pi pi-times"></i>
                    </button>
                )}

                {coloredHeader && title && (
                    <div className="animated-card__header-colored">
                        <h2 className="animated-card__header-colored-title">
                            {title}
                        </h2>
                    </div>
                )}

                {!coloredHeader && (title || subtitle) && (
                    <div className="animated-card__header">
                        {title && (
                            <h2 className="animated-card__title">{title}</h2>
                        )}
                        {subtitle && (
                            <p className="animated-card__subtitle">
                                {subtitle}
                            </p>
                        )}
                    </div>
                )}

                <div className="animated-card__body">{children}</div>
            </div>
        </div>
    );
};

export default AnimatedCard;
